import { Head, Link, router } from "@inertiajs/react"
import { type FormEvent, useEffect, useRef, useState } from "react"
import { ChevronRight, Pin, Plus, Search, X } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import { Select } from "@/components/ui/select"

type PlaceRow = {
  id: number
  name_en: string | null
  name_de: string | null
  status: string
  destination_name: string | null
  category_label: string | null
}

type Filters = {
  q: string
  status: string
  destination_id: string
  category_id: string
}

type FilterOption = {
  id: number
  label: string
}

type FilterOptions = {
  statuses: string[]
  destinations: FilterOption[]
  categories: FilterOption[]
}

type PaginationData = {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
  from: number
  to: number
}

type Props = {
  places: PlaceRow[]
  filters: Filters
  filter_options: FilterOptions
  pagination: PaginationData
}

export default function AdminPlacesIndex({
  places,
  filters,
  filter_options,
  pagination,
}: Props) {
  const [query, setQuery] = useState(filters.q)
  const searchTimeout = useRef<number | undefined>(undefined)
  const hasActiveFilters = Object.values(filters).some(Boolean)

  useEffect(() => {
    setQuery(filters.q)
  }, [filters.q])

  useEffect(() => {
    const normalizedQuery = query.trim()
    if (normalizedQuery === filters.q) return

    searchTimeout.current = window.setTimeout(() => {
      visit({ ...filters, q: normalizedQuery })
    }, 300)

    return () => window.clearTimeout(searchTimeout.current)
  }, [query, filters])

  const submitSearch = (event: FormEvent) => {
    event.preventDefault()
    window.clearTimeout(searchTimeout.current)
    visit({ ...filters, q: query.trim() })
  }

  const updateFilter = (key: keyof Filters, value: string) => {
    window.clearTimeout(searchTimeout.current)
    visit({ ...filters, q: query.trim(), [key]: value })
  }

  const clearFilters = () => {
    setQuery("")
    visit({ q: "", status: "", destination_id: "", category_id: "" })
  }

  return (
    <>
      <Head title="Places">
        <meta
          name="description"
          content="Search, filter, and manage places in the Xplore Croatia field guide."
        />
        <meta property="og:title" content="Places" />
        <meta
          property="og:description"
          content="Search, filter, and manage places in the Xplore Croatia field guide."
        />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>Places</h1>
              <p className="mt-1">
                {pagination.total_count} {hasActiveFilters ? "matching " : ""}
                {pagination.total_count === 1 ? "place" : "places"} in the field guide.
              </p>
            </div>
            <Button asChild>
              <Link href="/admin/places/new">
                <Plus className="h-4 w-4" />
                New place
              </Link>
            </Button>
          </div>
        </div>

        <section aria-labelledby="place-filters-title" className="mt-6 border-b border-hairline pb-6">
          <h2 id="place-filters-title" className="sr-only">Filter places</h2>
          <form onSubmit={submitSearch} className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="md:col-span-2 lg:col-span-1">
              <label htmlFor="place-search">Search</label>
              <div className="relative mt-1">
                <Search
                  aria-hidden="true"
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted"
                />
                <Input
                  id="place-search"
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Name or slug"
                  className="pl-9"
                />
              </div>
            </div>

            <div>
              <label htmlFor="place-status">Status</label>
              <Select
                id="place-status"
                value={filters.status}
                onChange={(event) => updateFilter("status", event.target.value)}
                className="mt-1"
              >
                <option value="">All statuses</option>
                {filter_options.statuses.map((status) => (
                  <option key={status} value={status}>{titleize(status)}</option>
                ))}
              </Select>
            </div>

            <div>
              <label htmlFor="place-destination">Destination</label>
              <Select
                id="place-destination"
                value={filters.destination_id}
                onChange={(event) => updateFilter("destination_id", event.target.value)}
                className="mt-1"
              >
                <option value="">All destinations</option>
                {filter_options.destinations.map((destination) => (
                  <option key={destination.id} value={destination.id}>{destination.label}</option>
                ))}
              </Select>
            </div>

            <div>
              <label htmlFor="place-category">Category</label>
              <Select
                id="place-category"
                value={filters.category_id}
                onChange={(event) => updateFilter("category_id", event.target.value)}
                className="mt-1"
              >
                <option value="">All categories</option>
                {filter_options.categories.map((category) => (
                  <option key={category.id} value={category.id}>{category.label}</option>
                ))}
              </Select>
            </div>
          </form>

          {hasActiveFilters && (
            <Button type="button" variant="ghost" size="sm" onClick={clearFilters} className="mt-3">
              <X className="h-4 w-4" />
              Clear filters
            </Button>
          )}
        </section>

        {places.length > 0 ? (
          <>
            <div className="mt-6 flex items-center justify-between gap-4">
              <p>
                Showing {pagination.from}–{pagination.to} of {pagination.total_count}
              </p>
              <p>{pagination.per_page} per page</p>
            </div>

            <ul className="mt-3 divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-page">
              {places.map((place) => (
                <li key={place.id}>
                  <Link
                    href={`/admin/places/${place.id}`}
                    className="flex items-center gap-3 px-4 py-3 no-underline hover:bg-surface"
                  >
                    <Pin className="h-4 w-4 shrink-0 text-ink-muted" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-ink-display">
                        {place.name_en ?? place.name_de}
                      </div>
                      <div className="truncate text-xs text-ink-muted">
                        {place.category_label} in {place.destination_name}
                      </div>
                    </div>
                    <Badge tone={place.status === "published" ? "accent" : "muted"}>
                      {titleize(place.status)}
                    </Badge>
                    <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" />
                  </Link>
                </li>
              ))}
            </ul>

            {pagination.total_pages > 1 && (
              <PlacesPagination filters={filters} pagination={pagination} />
            )}
          </>
        ) : (
          <div className="py-16 text-center">
            <h2>No places found</h2>
            <p className="mt-2">Try a different search or clear the active filters.</p>
            {hasActiveFilters && (
              <Button type="button" variant="secondary" onClick={clearFilters} className="mt-5">
                Clear filters
              </Button>
            )}
          </div>
        )}
      </AdminShell>
    </>
  )
}

function PlacesPagination({
  filters,
  pagination,
}: {
  filters: Filters
  pagination: PaginationData
}) {
  const items = paginationItems(pagination.current_page, pagination.total_pages)

  return (
    <Pagination className="mt-6">
      <PaginationContent>
        <PaginationItem>
          {pagination.current_page > 1 ? (
            <PaginationPrevious href={placesUrl(filters, pagination.current_page - 1)} />
          ) : (
            <PaginationPrevious
              aria-disabled="true"
              className="pointer-events-none opacity-50"
              tabIndex={-1}
            />
          )}
        </PaginationItem>

        {items.map((item, index) => (
          <PaginationItem key={`${item}-${index}`} className="hidden sm:block">
            {item === "ellipsis" ? (
              <PaginationEllipsis />
            ) : (
              <PaginationLink
                href={placesUrl(filters, item)}
                isActive={item === pagination.current_page}
                aria-label={`Go to page ${item}`}
              >
                {item}
              </PaginationLink>
            )}
          </PaginationItem>
        ))}

        <PaginationItem className="sm:hidden">
          <span className="px-2 text-sm text-ink-muted">
            Page {pagination.current_page} of {pagination.total_pages}
          </span>
        </PaginationItem>

        <PaginationItem>
          {pagination.current_page < pagination.total_pages ? (
            <PaginationNext href={placesUrl(filters, pagination.current_page + 1)} />
          ) : (
            <PaginationNext
              aria-disabled="true"
              className="pointer-events-none opacity-50"
              tabIndex={-1}
            />
          )}
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  )
}

function visit(filters: Filters) {
  const data = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== ""),
  )

  router.get("/admin/places", data, {
    only: ["places", "filters", "pagination"],
    preserveScroll: true,
    preserveState: true,
    replace: true,
  })
}

function placesUrl(filters: Filters, page: number) {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.set(key, value)
  })
  if (page > 1) params.set("page", String(page))
  const query = params.toString()
  return query ? `/admin/places?${query}` : "/admin/places"
}

function paginationItems(currentPage: number, totalPages: number): Array<number | "ellipsis"> {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1)
  }

  const pages = new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1])
  const sortedPages = [...pages].filter((page) => page > 0 && page <= totalPages).sort((a, b) => a - b)
  const items: Array<number | "ellipsis"> = []

  sortedPages.forEach((page, index) => {
    if (index > 0 && page - sortedPages[index - 1] > 1) items.push("ellipsis")
    items.push(page)
  })

  return items
}

function titleize(value: string) {
  return value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())
}
